import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search, Pencil, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  { value: 'oils', label: 'Oils & Fluids' },
  { value: 'filters', label: 'Filters' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'engine', label: 'Engine Parts' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'other', label: 'Other' }
];

const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'litre', label: 'Litre' },
  { value: 'set', label: 'Set' },
  { value: 'pack', label: 'Pack' },
  { value: 'kg', label: 'Kg' }
];

export default function PartsCatalog() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState([]);
  const [totalParts, setTotalParts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Dialog state
  const [showPartDialog, setShowPartDialog] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    part_number: '',
    description: '',
    unit_price: '',
    unit: 'each',
    category: '',
    supplier: ''
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchParts();
    }
  }, [user, searchQuery, categoryFilter]);

  const fetchParts = async () => {
    try {
      const params = new URLSearchParams({ ...getSiteParams() });
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const response = await fetch(`${BACKEND_URL}/api/parts-catalog?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setParts(data.parts || []);
        setTotalParts(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch parts:', error);
      toast.error('Failed to load parts');
    }
  };

  const openCreateDialog = () => {
    setEditingPart(null);
    setFormData({
      part_number: '',
      description: '',
      unit_price: '',
      unit: 'each',
      category: '',
      supplier: ''
    });
    setShowPartDialog(true);
  };

  const openEditDialog = (part) => {
    setEditingPart(part);
    setFormData({
      part_number: part.part_number || '',
      description: part.description || '',
      unit_price: part.unit_price?.toString() || '',
      unit: part.unit || 'each',
      category: part.category || '',
      supplier: part.supplier || ''
    });
    setShowPartDialog(true);
  };

  const handleSave = async () => {
    if (!formData.part_number || !formData.description || !formData.unit_price) {
      toast.error('Please fill in part number, description, and price');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        unit_price: parseFloat(formData.unit_price) || 0
      };

      let response;
      if (editingPart) {
        response = await fetch(`${BACKEND_URL}/api/parts-catalog/${editingPart.part_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(`${BACKEND_URL}/api/parts-catalog`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
      }

      if (response.ok) {
        toast.success(editingPart ? 'Part updated' : 'Part created');
        setShowPartDialog(false);
        fetchParts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save part');
      }
    } catch (error) {
      console.error('Failed to save part:', error);
      toast.error('Failed to save part');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (part) => {
    if (!window.confirm(`Delete part "${part.description}"?`)) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/parts-catalog/${part.part_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Part deleted');
        fetchParts();
      } else {
        toast.error('Failed to delete part');
      }
    } catch (error) {
      console.error('Failed to delete part:', error);
      toast.error('Failed to delete part');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="parts-catalog-container">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-['Chivo'] font-bold text-2xl sm:text-3xl text-slate-900">
              Parts Catalog
            </h1>
            <p className="text-slate-500">{totalParts} parts total</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="add-part-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Part
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by part number or description..."
                className="pl-10"
                data-testid="parts-search-input"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Parts Table */}
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Part Number</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500">No parts found</p>
                    <Button variant="link" onClick={openCreateDialog}>Add your first part</Button>
                  </TableCell>
                </TableRow>
              ) : (
                parts.map(part => (
                  <TableRow key={part.part_id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-sm">{part.part_number}</TableCell>
                    <TableCell>{part.description}</TableCell>
                    <TableCell className="capitalize">{part.category || '-'}</TableCell>
                    <TableCell className="capitalize">{part.unit}</TableCell>
                    <TableCell className="text-right font-medium">${part.unit_price?.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => openEditDialog(part)}
                          data-testid={`edit-part-${part.part_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {user?.role === 'super_admin' && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(part)}
                            data-testid={`delete-part-${part.part_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Part Dialog */}
        <Dialog open={showPartDialog} onOpenChange={setShowPartDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPart ? 'Edit Part' : 'Add New Part'}</DialogTitle>
              <DialogDescription>
                {editingPart ? 'Update part details' : 'Add a new part to your catalog'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="part_number">Part Number *</Label>
                  <Input
                    id="part_number"
                    value={formData.part_number}
                    onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                    placeholder="e.g., OIL-5W30"
                    data-testid="part-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_price">Unit Price *</Label>
                  <Input
                    id="unit_price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                    placeholder="0.00"
                    data-testid="unit-price-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Part description"
                  data-testid="description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select 
                    value={formData.category || 'other'} 
                    onValueChange={(val) => setFormData({ ...formData, category: val })}
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select 
                    value={formData.unit} 
                    onValueChange={(val) => setFormData({ ...formData, unit: val })}
                  >
                    <SelectTrigger data-testid="unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map(u => (
                        <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier (optional)</Label>
                <Input
                  id="supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="Supplier name"
                  data-testid="supplier-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPartDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-part-btn">
                {saving ? 'Saving...' : (editingPart ? 'Update' : 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
